/**
 * Petit Toi cart page.
 *
 * A single `<pt-cart-app>` custom element owns every mutation (quantity,
 * remove, card edit, in-memory toggle, hide-prices, discount code) and the
 * rush guard. Every mutation re-renders this section through the Section
 * Rendering API and dispatches the same `CartLinesUpdateEvent` Horizon's own
 * cart-icon listens for, so the header bubble stays correct without a full
 * page reload.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import { CARD_LANGUAGES, validateCardMessage } from '@pt/pt-card-rules';
import { isRushAllowed } from '@pt/pt-arrive-by';

/** @param {string} url @param {object} body */
async function postJSON(url, body) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  return response.json();
}

/** Fetches the live cart so a mutation can send the full existing state back. */
async function getCart() {
  return fetch('/cart.js').then((response) => response.json());
}

class PtCartApp extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #mobileBarObserver = null;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onDiscountSubmit);
    this.addEventListener('pt:cart-mutated', this.#onExternalMutation);
    this.#setupMobileBar();
    this.#runRushGuard().catch((error) => console.warn('[pt-cart]', error));
  }

  /**
   * Mobile sticky checkout bar (K's `.mbar`): hides itself once the real
   * Checkout button scrolls into view, shows again once it scrolls out.
   * Re-run after every section refresh since `#refreshSection` replaces
   * both nodes with fresh server-rendered markup (new Total included).
   */
  #setupMobileBar() {
    this.#mobileBarObserver?.disconnect();
    this.#mobileBarObserver = null;

    const bar = this.querySelector('[data-pt-mobile-bar]');
    const target = this.querySelector('.pt-cart__checkout');
    if (!bar || !target || !('IntersectionObserver' in window)) return;

    this.#mobileBarObserver = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('pt-cart__mobile-bar--hide', entry.isIntersecting);
    });
    this.#mobileBarObserver.observe(target);
  }

  /** @param {string} message */
  #setStatus(message) {
    const status = document.getElementById('pt-cart-status');
    if (status) status.textContent = message;
  }

  #setBusy(busy) {
    this.classList.toggle('pt-cart-app--busy', busy);
    this.toggleAttribute('aria-busy', busy);
  }

  /**
   * Announces the mutation the same way Horizon's own cart components do,
   * so `cart-icon` (listening on `document`) updates the header bubble.
   * @param {object} data - The parsed JSON response from a cart AJAX call.
   */
  #notifyCartUpdate(data) {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { itemCount: data.item_count, source: 'pt-cart' },
    });
  }

  /**
   * Re-fetches this section's markup and swaps only this element's contents.
   * @param {{ skipRushGuard?: boolean }} [options] - `skipRushGuard` is set by
   *   {@link #runRushGuard}'s own mutation so its fix-up refresh never
   *   re-triggers the guard: without it, a rush update that fails to clear
   *   the offending line (or a persistently failing request) would refresh,
   *   re-run the guard, and loop forever.
   */
  async #refreshSection({ skipRushGuard = false } = {}) {
    if (!this.sectionId) return;
    const url = `${location.pathname}?section_id=${this.sectionId}`;
    const response = await fetch(url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('pt-cart-app');
    if (fresh) {
      const arriveByState = this.#captureArriveByState();
      this.innerHTML = fresh.innerHTML;
      this.#restoreArriveByState(arriveByState);
      this.#setupMobileBar();
    }
    if (!skipRushGuard) {
      await this.#runRushGuard();
    }
  }

  /**
   * Captures the arrive-by occasion/date inputs' live value and focus state
   * before an innerHTML swap so a section refresh triggered by an unrelated
   * mutation (quantity, remove, card edit) never discards mid-typing text or
   * steals focus from the customer.
   */
  #captureArriveByState() {
    const occasionInput = this.querySelector('[data-pt-arrive-occasion]');
    const dateInput = this.querySelector('[data-pt-arrive-date]');
    const active = document.activeElement;
    const focus = active === occasionInput ? 'occasion' : active === dateInput ? 'date' : null;
    return {
      occasion: occasionInput instanceof HTMLInputElement ? occasionInput.value : null,
      date: dateInput instanceof HTMLInputElement ? dateInput.value : null,
      focus,
      selectionStart: focus && active instanceof HTMLInputElement ? active.selectionStart : null,
    };
  }

  /** @param {{occasion: string|null, date: string|null, focus: string|null, selectionStart: number|null}} state */
  #restoreArriveByState(state) {
    const occasionInput = this.querySelector('[data-pt-arrive-occasion]');
    const dateInput = this.querySelector('[data-pt-arrive-date]');

    if (state.occasion !== null && occasionInput instanceof HTMLInputElement && occasionInput.value !== state.occasion) {
      occasionInput.value = state.occasion;
      occasionInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (state.date !== null && dateInput instanceof HTMLInputElement && dateInput.value !== state.date) {
      dateInput.value = state.date;
      dateInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const target = state.focus === 'occasion' ? occasionInput : state.focus === 'date' ? dateInput : null;
    if (target instanceof HTMLInputElement) {
      target.focus();
      if (state.selectionStart != null && typeof target.setSelectionRange === 'function') {
        target.setSelectionRange(state.selectionStart, state.selectionStart);
      }
    }
  }

  /**
   * Runs a cart mutation, then notifies and refreshes on success.
   * @param {() => Promise<object>} request - Performs the AJAX call and returns the parsed response.
   * @param {{ skipRushGuard?: boolean }} [refreshOptions] - Forwarded to {@link #refreshSection}.
   */
  async #mutate(request, refreshOptions) {
    this.#setBusy(true);
    try {
      const data = await request();
      if (data && data.status) {
        this.#setStatus(data.description || data.message || "That didn't work — please try again.");
        return null;
      }
      this.#notifyCartUpdate(data);
      await this.#refreshSection(refreshOptions);
      return data;
    } catch (error) {
      console.warn('[pt-cart]', error);
      this.#setStatus("That didn't work — please try again.");
      return null;
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Checks every rush add-on line against its parent doll's size, and — while
   * the weekly order cap is active — against the cap itself, since rush
   * can't be honored during a capped week regardless of size. Manual cart
   * edits (or a stale add from before the cap flipped on) can leave a rush
   * line behind; this removes it and explains why.
   *
   * Runs at most once per render: it is only ever invoked by
   * {@link connectedCallback} (the initial render) or by
   * {@link #refreshSection} for a render it didn't itself cause. Its own
   * fix-up mutation refreshes with `skipRushGuard: true`, so a failed or
   * ineffective update can never re-trigger the guard and loop. It goes
   * through {@link #mutate}, which checks the `/cart/update.js` response
   * status and never lets a request/network error escape as an unhandled
   * rejection.
   */
  async #runRushGuard() {
    const capped = this.querySelector('pt-arrive-by')?.dataset.capReached === 'true';
    const toZero = {};
    for (const line of this.querySelectorAll('[data-pt-line][data-doll-id]')) {
      const rushKey = line.dataset.rushAddonKey;
      if (!rushKey) continue;

      if (capped) {
        toZero[rushKey] = 0;
        continue;
      }

      /** @type {string[]} */
      let allowedSizes = [];
      try {
        const parsed = JSON.parse(line.dataset.rushAllowedSizes || 'null');
        if (Array.isArray(parsed)) allowedSizes = parsed;
      } catch (error) {
        allowedSizes = [];
      }
      const hasSizeOption = line.dataset.hasSizeOption === 'true';
      const size = line.dataset.size || '';

      if (!isRushAllowed(hasSizeOption, allowedSizes, size)) {
        toZero[rushKey] = 0;
      }
    }

    const keys = Object.keys(toZero);
    if (keys.length === 0) return;

    const note = capped
      ? "Rush isn't available this week, so we removed it — we'll confirm your new delivery date."
      : this.querySelector('pt-arrive-by')?.dataset.rushNote;
    const result = await this.#mutate(() => postJSON(Theme.routes.cart_update_url, { updates: toZero }), {
      skipRushGuard: true,
    });
    // On failure #mutate already reported its own error and skipped the
    // refresh; only overwrite the status with the rush-specific message once
    // the removal actually went through.
    if (result) {
      this.#setStatus(note || 'Rush isn’t available for one of your dolls, so we removed it.');
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = /** @type {HTMLElement} */ (event.target);

    const increase = target.closest('[data-pt-qty-increase]');
    const decrease = target.closest('[data-pt-qty-decrease]');
    if (increase || decrease) {
      event.preventDefault();
      this.#changeQuantity(/** @type {HTMLElement} */ (increase || decrease), increase ? 1 : -1);
      return;
    }

    // Checked before [data-pt-remove]: the add-on button sits inside its doll's
    // line, and removing the doll would take every add-on with it.
    const removeAddon = target.closest('[data-pt-remove-addon]');
    if (removeAddon instanceof HTMLElement) {
      event.preventDefault();
      this.#removeAddon(removeAddon);
      return;
    }

    const remove = target.closest('[data-pt-remove]');
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      this.#removeLine(remove);
      return;
    }

    const openCard = target.closest('[data-pt-open-card-editor]');
    if (openCard) {
      event.preventDefault();
      this.#openCardDialog(openCard);
      return;
    }

    const cancelCard = target.closest('[data-pt-card-cancel]');
    if (cancelCard) {
      event.preventDefault();
      cancelCard.closest('dialog')?.close();
      return;
    }

    const saveCard = target.closest('[data-pt-card-save]');
    if (saveCard instanceof HTMLElement) {
      event.preventDefault();
      this.#saveCard(saveCard);
      return;
    }

    const oftenAdd = target.closest('[data-pt-often-add]');
    if (oftenAdd instanceof HTMLElement) {
      event.preventDefault();
      this.#addOftenAddedItem(oftenAdd);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.matches('[data-pt-in-memory]')) {
      this.#toggleInMemory(/** @type {HTMLInputElement} */ (target));
      return;
    }

    if (target.matches('[data-pt-hide-prices]')) {
      this.#toggleHidePrices(/** @type {HTMLInputElement} */ (target));
    }
  };

  /** @param {SubmitEvent} event */
  #onDiscountSubmit = (event) => {
    const form = /** @type {HTMLElement} */ (event.target);
    if (!form.matches('[data-pt-discount-form]')) return;
    event.preventDefault();
    this.#applyDiscount(/** @type {HTMLFormElement} */ (form));
  };

  /** Rush add-ons added by `pt-arrive-by` bubble this event up instead of duplicating the refresh logic. */
  #onExternalMutation = (event) => {
    const data = /** @type {CustomEvent} */ (event).detail?.data;
    if (!data) return;
    this.#notifyCartUpdate(data);
    this.#refreshSection().catch((error) => console.warn('[pt-cart]', error));
  };

  /**
   * Adds an "Often added" add-on to the cart, tied to the doll it belongs
   * to via `_for_doll` (the same contract key add-ons carry from the
   * product page, which `pt-cart.liquid` groups by): a single-doll cart
   * links automatically, a multi-doll cart asks which doll via
   * {@link #showDollPicker}, and a doll-less cart adds it as a plain
   * whole-order line. The section only renders this button for `Add-on`
   * products; face-bearing products link to their product form instead.
   * @param {HTMLElement} button
   */
  async #addOftenAddedItem(button) {
    const variantId = button.dataset.variantId;
    // aria-disabled alone doesn't stop a second click from adding a second unit.
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');

    const dolls = this.#parseDollsData(button);
    /** @type {string|undefined} */
    let forDoll;
    if (dolls.length === 1) {
      forDoll = dolls[0].id;
    } else if (dolls.length > 1) {
      const chosen = await this.#showDollPicker(dolls);
      if (!chosen) {
        // Cancelled (Esc or the Cancel button): no mutation, re-enable for a retry.
        button.removeAttribute('aria-disabled');
        return;
      }
      forDoll = chosen;
    }

    await this.#mutate(async () => {
      const item = { id: Number(variantId), quantity: 1 };
      if (forDoll) item.properties = { _for_doll: forDoll };
      // /cart/add.js's response lacks item_count/total_price (same gap
      // documented on #saveCard's language add-on path); fetch a fresh
      // /cart.js afterward so #notifyCartUpdate gets real totals.
      const addData = await postJSON(Theme.routes.cart_add_url, { items: [item] });
      if (addData?.status) return addData;
      return getCart();
    });
    // On success the section refresh replaced the button; on failure re-enable it for a retry.
    button.removeAttribute('aria-disabled');
  }

  /**
   * Reads the doll id/label list an "Often added" button's container
   * carries in `data-pt-dolls` (written by `pt-cart.liquid` from every
   * cart line's `_doll_id` + `Name` property/title + size).
   * @param {HTMLElement} button
   * @returns {{id: string, label: string}[]}
   */
  #parseDollsData(button) {
    const container = button.closest('[data-pt-dolls]');
    if (!(container instanceof HTMLElement)) return [];
    try {
      const parsed = JSON.parse(container.dataset.ptDolls || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[pt-cart]', error);
      return [];
    }
  }

  /**
   * Shows the accessible doll picker (`<dialog>` gives Esc-to-cancel and
   * focus trapping natively) and resolves with the chosen doll's
   * `_doll_id`, or `null` if the customer cancels.
   * @param {{id: string, label: string}[]} dolls
   * @returns {Promise<string|null>}
   */
  #showDollPicker(dolls) {
    const dialog = this.querySelector('[data-pt-doll-picker]');
    const optionsContainer = dialog?.querySelector('[data-pt-doll-picker-options]');
    const confirmButton = dialog?.querySelector('[data-pt-doll-picker-confirm]');
    const cancelButton = dialog?.querySelector('[data-pt-doll-picker-cancel]');
    if (
      !(dialog instanceof HTMLDialogElement) ||
      !(optionsContainer instanceof HTMLElement) ||
      !(confirmButton instanceof HTMLButtonElement) ||
      !(cancelButton instanceof HTMLButtonElement)
    ) {
      return Promise.resolve(null);
    }

    optionsContainer.replaceChildren(
      ...dolls.map((doll, index) => {
        const optionId = `pt-doll-picker-option-${index}`;
        const label = document.createElement('label');
        label.className = 'pt-doll-picker__option';
        label.htmlFor = optionId;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'pt-doll-picker-choice';
        input.id = optionId;
        input.value = doll.id;
        const text = document.createElement('span');
        text.textContent = doll.label;
        label.append(input, text);
        return label;
      })
    );
    confirmButton.disabled = true;

    return new Promise((resolve) => {
      const onChange = () => {
        confirmButton.disabled = !optionsContainer.querySelector('input:checked');
      };
      const onConfirm = () => {
        const checked = optionsContainer.querySelector('input:checked');
        dialog.close(checked instanceof HTMLInputElement ? checked.value : '');
      };
      const onCancelClick = () => dialog.close('');
      const onClose = () => {
        optionsContainer.removeEventListener('change', onChange);
        confirmButton.removeEventListener('click', onConfirm);
        cancelButton.removeEventListener('click', onCancelClick);
        dialog.removeEventListener('close', onClose);
        resolve(dialog.returnValue || null);
      };

      optionsContainer.addEventListener('change', onChange);
      confirmButton.addEventListener('click', onConfirm);
      cancelButton.addEventListener('click', onCancelClick);
      dialog.addEventListener('close', onClose);

      dialog.returnValue = '';
      dialog.showModal();
      const firstInput = optionsContainer.querySelector('input');
      if (firstInput instanceof HTMLInputElement) firstInput.focus();
    });
  }

  /** @param {HTMLElement} button @param {1 | -1} direction */
  async #changeQuantity(button, direction) {
    const stepper = button.closest('[data-pt-qty-stepper]');
    if (!(stepper instanceof HTMLElement)) return;
    const key = stepper.dataset.lineKey;
    if (!key) return;

    const valueEl = stepper.querySelector('[data-pt-qty-value]');
    const current = parseInt(valueEl?.textContent || '1', 10);
    const next = Math.max(1, current + direction);
    if (next === current) return;

    await this.#mutate(() => postJSON(Theme.routes.cart_change_url, { id: key, quantity: next }));
  }

  /** @param {HTMLElement} link */
  async #removeLine(link) {
    const key = link.dataset.lineKey;
    if (!key) return;

    const line = link.closest('[data-pt-line]');
    const addonKeys = line instanceof HTMLElement ? (line.dataset.addonKeys || '').split(',').filter(Boolean) : [];

    if (addonKeys.length === 0) {
      await this.#mutate(() => postJSON(Theme.routes.cart_change_url, { id: key, quantity: 0 }));
      return;
    }

    const updates = { [key]: 0 };
    for (const addonKey of addonKeys) updates[addonKey] = 0;
    await this.#mutate(() => postJSON(Theme.routes.cart_update_url, { updates }));
  }

  /** @param {HTMLElement} button */
  async #removeAddon(button) {
    const key = button.dataset.lineKey;
    if (!key) return;
    await this.#mutate(() => postJSON(Theme.routes.cart_change_url, { id: key, quantity: 0 }));
  }

  /** @param {HTMLInputElement} checkbox */
  async #toggleInMemory(checkbox) {
    const key = checkbox.dataset.lineKey;
    if (!key) return;

    await this.#mutate(async () => {
      const cart = await getCart();
      const line = cart.items.find((item) => item.key === key);
      if (!line) return { status: 422, message: 'Line not found' };

      const properties = { ...line.properties, 'In memory': checkbox.checked ? 'Yes' : 'No' };
      return postJSON(Theme.routes.cart_change_url, { id: key, quantity: line.quantity, properties });
    });
  }

  /** @param {HTMLInputElement} checkbox */
  async #toggleHidePrices(checkbox) {
    await this.#mutate(async () => {
      const cart = await getCart();
      const attributes = { ...cart.attributes, 'Hide prices': checkbox.checked ? 'Yes' : 'No' };
      return postJSON(Theme.routes.cart_update_url, { attributes });
    });
  }

  /** @param {HTMLElement} trigger */
  #openCardDialog(trigger) {
    const line = trigger.closest('[data-pt-line]');
    const dialog = line?.querySelector('dialog[data-pt-card-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const textarea = dialog.querySelector('[data-pt-card-textarea]');
    const counter = dialog.querySelector('[data-pt-card-counter]');
    const error = dialog.querySelector('[data-pt-card-error]');
    if (error) error.hidden = true;

    const updateCounter = () => {
      if (textarea instanceof HTMLTextAreaElement && counter) {
        counter.textContent = `${textarea.value.length} / 250`;
      }
    };
    updateCounter();
    textarea?.addEventListener('input', updateCounter);

    dialog.showModal();
    if (textarea instanceof HTMLTextAreaElement) textarea.focus();
  }

  /** @param {HTMLElement} saveButton */
  async #saveCard(saveButton) {
    const form = saveButton.closest('[data-pt-card-form]');
    const dialog = saveButton.closest('dialog');
    if (!(form instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;

    const textarea = form.querySelector('[data-pt-card-textarea]');
    const languageSelect = form.querySelector('[data-pt-card-language]');
    const errorEl = form.querySelector('[data-pt-card-error]');
    const cancelButton = form.querySelector('[data-pt-card-cancel]');
    if (!(textarea instanceof HTMLTextAreaElement) || !(languageSelect instanceof HTMLSelectElement)) return;

    // An empty message falls back to the doll's segment card_default (or the
    // shared fallback), same as the product page.
    const message = textarea.value.trim() || form.dataset.cardDefault || '';
    const language = CARD_LANGUAGES.includes(languageSelect.value) ? languageSelect.value : 'English';

    const { ok, error } = validateCardMessage(message);
    if (!ok) {
      if (errorEl) {
        errorEl.textContent = error;
        errorEl.hidden = false;
      }
      return;
    }
    if (errorEl) errorEl.hidden = true;

    const key = form.dataset.lineKey;
    const dollId = form.dataset.dollId;
    if (!key) return;

    saveButton.disabled = true;
    if (cancelButton instanceof HTMLButtonElement) cancelButton.disabled = true;

    const result = await this.#mutate(async () => {
      // Re-resolve the line from a fresh cart: a property change earlier in
      // this same session changes the line's key, so a stale key would miss.
      const cart = await getCart();
      const line = cart.items.find((item) => item.key === key);
      if (!line) return { status: 422, message: 'Line not found — please refresh and try again.' };

      const previousProperties = { ...line.properties };
      const previousLanguage = previousProperties['Card language'] || 'English';
      const properties = { ...previousProperties, 'Card message': message, 'Card language': language };

      const changeData = await postJSON(Theme.routes.cart_change_url, {
        id: key,
        quantity: line.quantity,
        properties,
      });
      if (changeData?.status) return changeData;

      const wasEnglish = previousLanguage === 'English';
      const isEnglish = language === 'English';
      const cardLanguageHandle = form.dataset.cardLanguageHandle;
      const cardLanguageVariantId = form.dataset.cardLanguageVariantId;

      /**
       * Reverts the property change just applied above, so a failing
       * add-on step never leaves the card message/language half-changed
       * from the customer's point of view.
       */
      const rollback = async () => {
        // The property change above gave the line a new key; find it again by its doll id.
        const current = dollId ? (await getCart()).items.find((item) => item.properties?._doll_id === dollId) : null;
        return postJSON(Theme.routes.cart_change_url, {
          id: current?.key || key,
          quantity: current?.quantity || line.quantity,
          properties: previousProperties,
        });
      };

      try {
        if (wasEnglish && !isEnglish && cardLanguageVariantId && dollId) {
          const refreshedCart = await getCart();
          const alreadyHasAddon = refreshedCart.items.some(
            (item) => item.properties?._for_doll === dollId && item.handle === cardLanguageHandle
          );
          if (!alreadyHasAddon) {
            const addData = await postJSON(Theme.routes.cart_add_url, {
              items: [{ id: Number(cardLanguageVariantId), quantity: 1, properties: { _for_doll: dollId } }],
            });
            if (addData?.status) {
              await rollback();
              return {
                status: addData.status,
                message: addData.message || addData.description || "Couldn't add the language add-on — your card wasn't changed.",
              };
            }
            // /cart/add.js's response lacks item_count/total_price; use a fresh /cart.js for the bubble update.
            return getCart();
          }
        } else if (!wasEnglish && isEnglish && dollId) {
          const refreshedCart = await getCart();
          const addon = refreshedCart.items.find(
            (item) => item.properties?._for_doll === dollId && item.handle === cardLanguageHandle
          );
          if (addon) {
            const removeData = await postJSON(Theme.routes.cart_update_url, { updates: { [addon.key]: 0 } });
            if (removeData?.status) {
              await rollback();
              return {
                status: removeData.status,
                message: removeData.message || removeData.description || "Couldn't update the language add-on — your card wasn't changed.",
              };
            }
            return removeData;
          }
        }
      } catch (fetchError) {
        console.warn('[pt-cart]', fetchError);
        await rollback();
        return { status: 'error', message: "That didn't work — your card wasn't changed." };
      }

      return changeData;
    });

    saveButton.disabled = false;
    if (cancelButton instanceof HTMLButtonElement) cancelButton.disabled = false;

    // On failure #mutate already announced the error and skipped the section
    // refresh; leave the dialog open (with the stale data intact) so the
    // customer can retry instead of silently losing their edit.
    if (result) dialog.close();
  }

  /**
   * Doesn't use the generic `#mutate` helper: the "code not applicable"
   * response isn't a failure (no section refresh should be skipped), but the
   * error element it needs to fill in lives in the freshly-fetched section
   * HTML, so it has to be looked up again after the refresh.
   * @param {HTMLFormElement} form
   */
  async #applyDiscount(form) {
    const input = form.querySelector('[data-pt-discount-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const code = input.value.trim();
    if (!code) return;

    this.#setBusy(true);
    try {
      const data = await postJSON(Theme.routes.cart_update_url, { discount: code });
      if (data && data.status) {
        this.#setStatus(data.description || data.message || "That didn't work — please try again.");
        return;
      }

      this.#notifyCartUpdate(data);
      await this.#refreshSection();

      const applied = data.discount_codes?.find((entry) => entry.code === code);
      if (applied && applied.applicable === false) {
        const errorEl = this.querySelector('[data-pt-discount-error]');
        if (errorEl instanceof HTMLElement) {
          errorEl.textContent = `The code "${code}" isn't valid or doesn't apply to your cart.`;
          errorEl.hidden = false;
        }
      }
    } catch (error) {
      console.warn('[pt-cart]', error);
      this.#setStatus("That didn't work — please try again.");
    } finally {
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('pt-cart-app')) {
  customElements.define('pt-cart-app', PtCartApp);
}
